import { useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { anmelden, netconAnmelden } from '../lib/auth'
import { nachholenVormerken, netconCloudAnmelden } from '../lib/netcon-cloud'
import { Marke } from '../components/Marke'
import { Button } from '../components/ui/primitives'

/**
 * Anmeldemaske (AP 0.5, erweitert am 24.08.2026).
 *
 * Es gibt bewusst keine Selbstregistrierung und kein „Passwort vergessen": Work-Konten legt die
 * Administration an (Konzept Kap. 3), und ein E-Mail-Versand ist noch nicht eingerichtet.
 *
 * ## Zwei Wege, ein Formular
 *
 * Seit dem 24.08.2026 kann man sich auch mit seinen **NetCon-Zugangsdaten** anmelden. Beide
 * Wege fragen dasselbe ab – Adresse und Passwort –, also gibt es **ein** Formular und darüber
 * die Wahl, wogegen geprüft wird. Zwei getrennte Masken hätten dieselben zwei Felder zweimal
 * gezeigt, und die Frage „welche nehme ich denn jetzt?" wäre die erste Hürde vor dem ersten
 * Klick.
 *
 * **NetCon steht vorn**, weil es der Weg ist, den jeder in der Entwicklung gehen kann, ohne dass
 * ihm jemand vorher ein Konto anlegt. Das Work-Passwort bleibt daneben sichtbar und ist kein
 * Restposten: Fachbereiche haben nicht zwingend ein NetCon-Konto, und fällt NetCon aus, ist es
 * der einzige Weg herein – auch für die Administration, die den Ausfall beheben müsste.
 */
type Weg = 'netcon' | 'work'

export function Anmelden() {
  const [weg, setWeg] = useState<Weg>('netcon')
  const [email, setEmail] = useState('')
  const [passwort, setPasswort] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const queryClient = useQueryClient()

  async function absenden(e: FormEvent): Promise<void> {
    e.preventDefault()
    setFehler(null)
    setLaeuft(true)
    try {
      if (weg === 'netcon') {
        const { weiter } = await netconAnmelden(email, passwort)
        /**
         * **Der Umweg über NetCon.Cloud** (17.09.2026,
         * [E-364](../../../../docs/ENTSCHEIDUNGEN.md#e-364)). Kein `invalidateQueries` davor:
         * die Seite wird gleich verlassen, und ein Nachladen kurz vor dem Sprung ist Arbeit für
         * einen Zustand, den niemand zu sehen bekommt. Das Portal schickt den Browser auf
         * dieselbe Seite zurück, dann läuft die Shell ohnehin neu an.
         *
         * **`location.assign` und kein `fetch`**: Es muss ein echter Seitenwechsel sein, sonst
         * entsteht im Browser keine Sitzung und der ganze Umweg war umsonst.
         *
         * Der Vermerk steht **vor** dem Absprung – danach läuft hier kein Code mehr. Er sorgt
         * dafür, dass Work nach der Rückkehr einmal still bei Keycloak anfragt: erst das legt
         * die Client-Sitzung an, ohne die ein Abmelden im Portal hier nichts bewirkt.
         */
        if (weiter !== undefined) {
          nachholenVormerken()
          window.location.assign(weiter)
          return
        }
      } else {
        await anmelden(email, passwort)
      }
      // Sitzung neu laden – die Shell entscheidet danach, was sie zeigt.
      await queryClient.invalidateQueries()
    } catch (error) {
      setFehler(error instanceof Error ? error.message : 'Anmeldung fehlgeschlagen.')
    } finally {
      setLaeuft(false)
    }
  }

  /** Der Wechsel räumt die Fehlermeldung weg: sie gehörte zum anderen Weg. */
  function wechseln(neu: Weg): void {
    setWeg(neu)
    setFehler(null)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-5">
      <div className="w-full min-w-0 max-w-[380px]">
        <div className="mb-6 flex items-center gap-2.5">
          <Marke className="size-9 rounded-[10px]" />
          <div>
            <div className="text-[17px] font-bold tracking-[-0.2px]">NetCon Work</div>
            <div className="text-[11px] text-muted">Aufgabensteuerung der Entwicklung</div>
          </div>
        </div>

        <form
          onSubmit={(e) => void absenden(e)}
          className="rounded-card border border-line bg-surface p-6 shadow-card"
        >
          <h1 className="mb-4 text-[15px] font-bold">Anmelden</h1>

          <div
            role="radiogroup"
            aria-label="Womit anmelden?"
            className="mb-4 flex gap-1 rounded-[9px] bg-page p-1"
          >
            {(
              [
                ['netcon', 'NetCon-Zugang'],
                ['work', 'Work-Passwort'],
              ] as const
            ).map(([wert, label]) => (
              <button
                key={wert}
                type="button"
                role="radio"
                aria-checked={weg === wert}
                onClick={() => wechseln(wert)}
                className={`flex-1 cursor-pointer rounded-[7px] px-2 py-1.5 text-[12px] font-semibold ${
                  weg === wert ? 'bg-surface text-ink shadow-task' : 'text-muted hover:text-ink2'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <label className="mb-3 block">
            <span className="mb-1 block text-[11px] font-semibold tracking-[0.4px] text-muted uppercase">
              E-Mail
            </span>
            <input
              type="email"
              required
              autoFocus
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-[9px] border border-line2 px-3 py-2 text-[13.5px] outline-none focus:border-accent"
            />
          </label>

          <label className="mb-4 block">
            <span className="mb-1 block text-[11px] font-semibold tracking-[0.4px] text-muted uppercase">
              {weg === 'netcon' ? 'NetCon-Passwort' : 'Work-Passwort'}
            </span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
              className="w-full rounded-[9px] border border-line2 px-3 py-2 text-[13.5px] outline-none focus:border-accent"
            />
          </label>

          {fehler && (
            <div
              role="alert"
              className="mb-3 rounded-[9px] bg-[#fdeaea] px-3 py-2 text-[12.5px] text-[#b02a2a]"
            >
              {fehler}
            </div>
          )}

          <Button type="submit" variant="primary" className="w-full" disabled={laeuft}>
            {laeuft ? 'Melde an …' : 'Anmelden'}
          </Button>

          {/**
           * **Der dritte Weg – additiv, nie ersetzend** (01.09.2026,
           * [E-289](../../../docs/ENTSCHEIDUNGEN.md#e-289)).
           *
           * Er steht **unter** dem Formular und nicht als dritter Reiter darüber: die beiden
           * oben sind zwei Arten, hier ein Passwort einzugeben. Dieser führt weg, auf eine
           * fremde Maske. Das ist ein anderer Vorgang, und er sieht deshalb anders aus.
           *
           * Das vorhandene Formular bleibt vollständig, wie es war. Es ist der Notausgang,
           * falls NetCon.Cloud ausfällt – und bleibt es, bis alle Konten verknüpft sind.
           */}
          <div className="mt-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-line" />
            <span className="text-[11px] text-muted">oder</span>
            <div className="h-px flex-1 bg-line" />
          </div>

          <button
            type="button"
            disabled={laeuft}
            onClick={() => {
              setFehler(null)
              void netconCloudAnmelden().catch(() =>
                setFehler('NetCon.Cloud war nicht erreichbar. Versuch es gleich noch einmal.'),
              )
            }}
            className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded-[9px] border border-line2 px-3 py-2 text-[13px] font-semibold text-ink2 hover:border-accent hover:text-ink disabled:cursor-default disabled:opacity-50"
          >
            <span
              aria-hidden="true"
              className="size-[9px] rounded-full"
              style={{ background: '#3B5BD0' }}
            />
            Mit NetCon.Cloud anmelden
          </button>
        </form>

        <p className="mt-4 text-center text-[11.5px] text-muted">
          {weg === 'netcon'
            ? 'Deine NetCon-Zugangsdaten. Work speichert das Passwort nicht.'
            : 'Kein Konto? Konten legt die Administration an.'}
        </p>
      </div>
    </div>
  )
}

/**
 * **Anmelden, wenn es nur noch NetCon.Cloud gibt** (05.11.2026,
 * [E-461](../../../docs/ENTSCHEIDUNGEN.md#e-461)).
 *
 * Gezeigt nur in zwei Fällen: **nach dem Abmelden** (sonst meldete die lebende Sitzung im
 * Anmeldedienst sofort wieder an) und wenn der direkte Sprung eben erst versucht wurde und der
 * Mensch zurückkam. Sonst führt `AppShell` ohne Umweg zum Anmeldedienst. Kein Formular – das
 * öffnet nur der Notausgang `LOKALE_ANMELDUNG` am Server.
 */
export function AnmeldenNurCloud({ abgemeldet }: { abgemeldet: boolean }) {
  const [fehler, setFehler] = useState<string | null>(null)
  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-5">
      <div className="w-full min-w-0 max-w-[380px]">
        <div className="mb-6 flex items-center gap-2.5">
          <Marke className="size-9 rounded-[10px]" />
          <div>
            <div className="text-[17px] font-bold tracking-[-0.2px]">NetCon Work</div>
            <div className="text-[11px] text-muted">Aufgabensteuerung der Entwicklung</div>
          </div>
        </div>
        <div className="rounded-card border border-line bg-surface p-6 shadow-card">
          <h1 className="mb-2 text-[15px] font-bold">{abgemeldet ? 'Abgemeldet' : 'Anmelden'}</h1>
          <p className="mb-4 text-[13px] text-ink2">
            {abgemeldet
              ? 'Du bist aus Work abgemeldet.'
              : 'Work meldet über NetCon.Cloud an – mit deinem NetCon-Zugang.'}
          </p>
          {fehler && (
            <p role="alert" className="mb-3 text-[12.5px] text-[#B42318]">
              {fehler}
            </p>
          )}
          <Button
            type="button"
            className="w-full justify-center"
            onClick={() => {
              setFehler(null)
              void netconCloudAnmelden().catch(() =>
                setFehler('NetCon.Cloud war nicht erreichbar. Versuch es gleich noch einmal.'),
              )
            }}
          >
            Mit NetCon.Cloud anmelden
          </Button>
        </div>
      </div>
    </div>
  )
}
